import { MessageCircle, MoreVertical, Star, Clock } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Avatar } from "@/components/ui/Avatar"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/utils/Functions"
import type { Contact } from "../lib/data"

interface ContactCardProps {
  contact: Contact
  onAction: (action: string) => void
}

export const ContactCard = ({ contact, onAction }: ContactCardProps) => {
  return (
    <div className="group relative bg-card border border-border hover:bg-muted/50 rounded-2xl p-5 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <Avatar
          src={contact.avatar}
          fallback={contact.name.charAt(0)}
          className="size-12 shrink-0"
        />
        <button
          onClick={() => onAction(`Star ${contact.name}`)}
          className="p-1.5 rounded-md text-muted-foreground/50 hover:text-yellow-400 transition-colors"
        >
          <Star className={cn("size-4", contact.isStarred && "fill-yellow-400 text-yellow-400")} />
        </button>
      </div>

      <h3 className="text-foreground font-medium mb-1 truncate">{contact.name}</h3>
      <p className="text-foreground/60 text-sm mb-1 truncate">{contact.role}</p>
      <p className="text-muted-foreground text-xs mb-3 truncate">{contact.company}</p>

      <div className="flex flex-wrap gap-1 mb-4">
        {contact.tags.slice(0, 3).map((tag) => (
          <Badge key={tag} variant="secondary" size="sm" className="text-xs">
            {tag}
          </Badge>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => onAction(`Message ${contact.name}`)}
        >
          <MessageCircle className="size-4 mr-1" />
          Message
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAction(`View ${contact.name} Profile`)}
        >
          <MoreVertical className="size-4" />
        </Button>
      </div>

      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
        <Clock className="size-3 text-muted-foreground/50" />
        <span className="text-muted-foreground/50 text-xs">Last contact {contact.lastContact}</span>
      </div>
    </div>
  )
}
